import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SpotifyConfig {
  url: string;
  embed_url: string;
}

export interface MusicaDoDia {
  nome: string;
  banda: string;
  youtube_id: string;
  youtube_url: string;
}

interface OverrideConfig extends MusicaDoDia {
  override_id?: string;
  override_expira_em?: string;
}

interface PlaylistItem {
  id: string;
  nome: string;
  banda: string;
  youtube_url: string;
  youtube_id: string;
  ordem: number;
  ativo: boolean;
  criado_em: string;
}

// Data base fixa para cálculo de rotação (1 de janeiro de 2026, meia-noite em SP)
const DATA_BASE = new Date('2026-01-01T03:00:00.000Z'); // 00:00 SP = 03:00 UTC

/**
 * Retorna a data atual no fuso de São Paulo como um Date "zerado" à meia-noite UTC
 * para cálculo de diferença de dias.
 */
const getSaoPauloMidnight = (): Date => {
  const now = new Date();
  // Obter data em SP usando Intl
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now); // "YYYY-MM-DD"
  const [y, m, d] = parts.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
};

const calcularIndiceDia = (totalMusicas: number): number => {
  if (totalMusicas <= 0) return 0;
  const hoje = getSaoPauloMidnight();
  const base = new Date(Date.UTC(
    DATA_BASE.getUTCFullYear(),
    DATA_BASE.getUTCMonth(),
    DATA_BASE.getUTCDate()
  ));
  const diasPassados = Math.floor((hoje.getTime() - base.getTime()) / 86400000);
  return ((diasPassados % totalMusicas) + totalMusicas) % totalMusicas; // garante positivo
};

export const useSiteConfig = () => {
  const [spotifyPlaylist, setSpotifyPlaylist] = useState<SpotifyConfig | null>(null);
  const [musicaDoDia, setMusicaDoDia] = useState<MusicaDoDia | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      // Buscar configs e playlist em paralelo
      const [configResult, playlistResult] = await Promise.all([
        supabase
          .from('site_config')
          .select('chave, valor')
          .in('chave', ['spotify_playlist', 'musica_do_dia']),
        supabase
          .from('musicas_do_dia_playlist' as any)
          .select('*')
          .eq('ativo', true)
          .order('ordem', { ascending: true }),
      ]);

      // Processar spotify
      configResult.data?.forEach((item) => {
        if (item.chave === 'spotify_playlist' && item.valor) {
          setSpotifyPlaylist(item.valor as unknown as SpotifyConfig);
        }
      });

      // Verificar override
      const overrideRow = configResult.data?.find(i => i.chave === 'musica_do_dia');
      const overrideConfig = overrideRow?.valor as unknown as OverrideConfig | null;

      const playlist = (playlistResult.data || []) as unknown as PlaylistItem[];

      // Lógica de seleção
      if (overrideConfig?.override_expira_em) {
        const expira = new Date(overrideConfig.override_expira_em);
        if (expira > new Date()) {
          // Override ativo
          setMusicaDoDia({
            nome: overrideConfig.nome,
            banda: overrideConfig.banda,
            youtube_id: overrideConfig.youtube_id,
            youtube_url: overrideConfig.youtube_url,
          });
          return;
        }
      }

      // Rotação automática
      if (playlist.length > 0) {
        const indice = calcularIndiceDia(playlist.length);
        const musica = playlist[indice];
        setMusicaDoDia({
          nome: musica.nome,
          banda: musica.banda,
          youtube_id: musica.youtube_id,
          youtube_url: musica.youtube_url,
        });
      } else if (overrideConfig?.youtube_id) {
        // Fallback: usar config manual se não há playlist
        setMusicaDoDia({
          nome: overrideConfig.nome,
          banda: overrideConfig.banda,
          youtube_id: overrideConfig.youtube_id,
          youtube_url: overrideConfig.youtube_url,
        });
      }
    } catch {
      // Silently fail
    } finally {
      setIsLoading(false);
    }
  };

  return {
    spotifyPlaylist,
    musicaDoDia,
    isLoading,
  };
};
